// SF4 — Festival Correlation Engine.
//
// What each event adds ON TOP of the underlying pattern, measured against what
// those same days were expected to sell anyway.
//
// FOUR STATES THAT MUST NOT LOOK ALIKE, which is most of this component:
//   measured    enough clean occurrences to size it
//   unsized     the event is expected but has too few clean occurrences
//   no effect   measured, and the answer is that it changes nothing
//   combined    every occurrence ran with a discount, so festival lift and
//               discount lift cannot be separated
// Collapsing "no effect" into "unsized" would tell a seller an event does
// nothing when the truth is that nobody knows yet.
import React, { useCallback, useContext, useEffect, useState } from 'react';
import { FiAlertTriangle, FiInfo } from 'react-icons/fi';

import AuthContext from '../../../../context/AuthContext';
import local from '../Seasonal.module.css';
import { getEvents } from '../services/seasonalApi';
import SyncProgress from '../../components/SyncProgress';

const STATUS = {
  measured: { label: 'measured', className: 'statusMeasured' },
  unsized: { label: 'not sized', className: 'statusUnsized' },
  no_effect: { label: 'no effect', className: 'statusNoEffect' },
  combined: { label: 'combined with a discount', className: 'statusCombined' },
};

const KIND = { festival: 'Festival', amazon_sale: 'Amazon sale event' };

const FestivalLift = ({ asin }) => {
  const authCtx = useContext(AuthContext);
  const token = authCtx?.token;

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    setError(null);
    try {
      setData(await getEvents(token, { asin }));
    } catch (err) {
      setError(err);
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [token, asin]);

  useEffect(() => { load(); }, [load]);

  const events = data?.events || [];
  const festivals = events.filter((event) => event.kind === 'festival');
  const sales = events.filter((event) => event.kind === 'amazon_sale');

  const renderGroup = (title, rows, caption) => (
    <div className={local.indexGroup}>
      <div className={local.indexGroupTitle}>{title}</div>
      {caption && <p className={local.muted}>{caption}</p>}
      <div className={local.tableScroll}>
        <table className={local.table}>
          <thead>
            <tr>
              <th>Event</th>
              <th className={local.numeric}>Lift</th>
              <th className={local.numeric}>Occurrences</th>
              <th>Consistency</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((event) => {
              const status = STATUS[event.status] || STATUS.unsized;
              return (
                <tr key={event.key}>
                  <td>
                    {event.name}
                    {event.participation_dependent && (
                      <span className={local.pill}>needs opting in</span>
                    )}
                    <div className={local.peakDriver}>{event.reason}</div>
                  </td>
                  <td className={local.numeric}>
                    {event.lift === null || event.lift === undefined
                      ? <span className={local.blank}>—</span>
                      : <strong>{event.lift.toFixed(2)}×</strong>}
                  </td>
                  <td className={local.numeric}>
                    {event.occurrences_measured} of {event.occurrences_known}
                  </td>
                  <td>
                    {event.consistency
                      ? <span className={event.consistency === 'high'
                        ? local.statusMeasured : local.statusCombined}>
                        {event.consistency === 'high' ? 'consistent' : 'varies'}
                      </span>
                      : <span className={local.blank}>—</span>}
                  </td>
                  <td className={local[status.className]}>{status.label}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );

  return (
    <section className={local.card}>
      <div className={local.cardHead}>
        <h2 className={local.cardTitle}>What each event adds</h2>
        {data?.calendar && (
          <span className={local.tierChip}>
            {data.calendar.events} events · {data.calendar.occurrences} dated occurrences
          </span>
        )}
      </div>
      <p className={local.muted}>
        Measured in units against what those same days were expected to sell
        anyway, so the season underneath is not counted as part of the event.
      </p>

      {error && (
        <div className={`${local.banner} ${error.reportUnavailable ? local.bannerWarn : local.bannerError}`}>
          <FiAlertTriangle className={local.bannerIcon} />
          <div>
            <strong>
              {error.notConnected ? 'No Amazon account connected'
                : error.progress ? 'Sync in progress'
                : error.reportUnavailable ? 'Data not ready' : 'Could not load events'}
            </strong>
            <div>{error.message}</div>
            <SyncProgress progress={error.progress} />
          </div>
        </div>
      )}

      {loading && !data && <div className={local.emptyCell}>Loading…</div>}

      {data && !data.available && (
        <div className={`${local.banner} ${local.bannerWarn}`}>
          <FiInfo className={local.bannerIcon} />
          <div>
            <strong>Not enough history to measure an event</strong>
            <div>{data.reason}</div>
          </div>
        </div>
      )}

      {data?.available && events.length === 0 && (
        <div className={local.emptyCell}>
          No events are on the calendar yet, so there is nothing to measure.
        </div>
      )}

      {data?.available && festivals.length > 0 && renderGroup(
        'Festivals', festivals,
        'Days a discount was running are excluded, so what is left is the '
        + 'festival rather than the deal.')}

      {data?.available && sales.length > 0 && renderGroup(
        'Amazon sale events', sales,
        'Discount days are kept here, because for these the discount is the '
        + 'event. The lift depends on you opting in and discounting again.')}

      {data?.notes?.length > 0 && (
        <ul className={local.noteList}>
          {data.notes.map((note) => (
            <li key={note}><FiInfo className={local.noteIcon} />{note}</li>
          ))}
        </ul>
      )}
    </section>
  );
};

export default FestivalLift;
